// src/features/admin/adminSubscriptionSlice.js
import axios from '../../utils/axios';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { dedupeEntities } from '../../utils/collectionUtils';

// ────────────────────────────────────────────────
// Thunks (Admin-only subscription actions)
// ────────────────────────────────────────────────

export const getAllSubscriptions = createAsyncThunk(
  'adminSubscription/getAllSubscriptions',
  async (
    { page = 1, limit = 20, status, plan, userId, sortBy, sortOrder },
    { rejectWithValue }
  ) => {
    try {
      const params = { page, limit };
      if (status) params.status = status;
      if (plan) params.plan = plan;
      if (userId) params.userId = userId;
      if (sortBy) params.sortBy = sortBy;
      if (sortOrder) params.sortOrder = sortOrder;

      const res = await axios.get('/subscriptions/admin/all', { params });
      return res.data.data || res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || 'Failed to fetch subscriptions'
      );
    }
  }
);

export const getSubscriptionById = createAsyncThunk(
  'adminSubscription/getSubscriptionById',
  async (subId, { rejectWithValue }) => {
    try {
      const res = await axios.get(`/subscriptions/admin/${subId}`);
      return res.data.data?.subscription || res.data.subscription || res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || 'Failed to fetch subscription details'
      );
    }
  }
);

export const updateSubscription = createAsyncThunk(
  'adminSubscription/updateSubscription',
  async ({ subId, updates }, { rejectWithValue }) => {
    try {
      const res = await axios.patch(`/subscriptions/admin/${subId}`, updates);
      return res.data.data?.subscription || res.data.subscription || res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || 'Failed to update subscription'
      );
    }
  }
);

export const cancelSubscriptionAdmin = createAsyncThunk(
  'adminSubscription/cancelSubscriptionAdmin',
  async ({ subId, reason }, { rejectWithValue }) => {
    try {
      const payload = reason ? { reason } : {};
      const res = await axios.post(`/subscriptions/admin/${subId}/cancel`, payload);
      return res.data.data || res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || 'Failed to cancel subscription (admin)'
      );
    }
  }
);

export const expireSubscriptionAdmin = createAsyncThunk(
  'adminSubscription/expireSubscriptionAdmin',
  async ({ subId, reason }, { rejectWithValue }) => {
    try {
      const payload = reason ? { reason } : {};
      const res = await axios.post(`/subscriptions/admin/${subId}/expire`, payload);
      return res.data.data?.subscription || res.data.subscription || res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || 'Failed to expire subscription (admin)'
      );
    }
  }
);

export const getSubscriptionStats = createAsyncThunk(
  'adminSubscription/getSubscriptionStats',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.get('/subscriptions/admin/stats');
      return res.data.data?.stats || res.data.stats || res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || 'Failed to fetch subscription statistics'
      );
    }
  }
);

// ────────────────────────────────────────────────
// Slice
// ────────────────────────────────────────────────

const initialState = {
  subscriptions: [],
  currentSubscription: null,
  total: 0,
  currentPage: 1,
  pagination: null,
  stats: null,
  loading: false,
  actionLoading: false,
  error: null,
  lastActionSubId: null,
};

const adminSubscriptionSlice = createSlice({
  name: 'adminSubscription',
  initialState,
  reducers: {
    clearAdminSubscriptionError: (state) => {
      state.error = null;
    },
    clearCurrentSubscription: (state) => {
      state.currentSubscription = null;
    },
  },
  extraReducers: (builder) => {
    // getAllSubscriptions
    builder
      .addCase(getAllSubscriptions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllSubscriptions.fulfilled, (state, action) => {
        state.loading = false;
        state.subscriptions = dedupeEntities(action.payload.subscriptions);
        state.total = action.payload.pagination?.total || 0;
        state.currentPage = action.payload.pagination?.page || 1;
        state.pagination = action.payload.pagination || null;
      })
      .addCase(getAllSubscriptions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // getSubscriptionById
    builder
      .addCase(getSubscriptionById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSubscriptionById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSubscription = action.payload;
      })
      .addCase(getSubscriptionById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // updateSubscription
    builder
      .addCase(updateSubscription.pending, (state, action) => {
        state.actionLoading = true;
        state.lastActionSubId = action.meta.arg.subId;
      })
      .addCase(updateSubscription.fulfilled, (state, action) => {
        state.actionLoading = false;
        const updated = action.payload;
        const id = updated._id;

        const index = state.subscriptions.findIndex((s) => s._id === id);
        if (index !== -1) {
          state.subscriptions[index] = { ...state.subscriptions[index], ...updated };
        }
        if (state.currentSubscription?._id === id) {
          state.currentSubscription = { ...state.currentSubscription, ...updated };
        }
        state.lastActionSubId = null;
      })
      .addCase(updateSubscription.rejected, (state) => {
        state.actionLoading = false;
        state.lastActionSubId = null;
      });

    // cancelSubscriptionAdmin
    builder
      .addCase(cancelSubscriptionAdmin.pending, (state, action) => {
        state.actionLoading = true;
        state.lastActionSubId = action.meta.arg.subId;
      })
      .addCase(cancelSubscriptionAdmin.fulfilled, (state, action) => {
        state.actionLoading = false;
        const updated = action.payload.subscription || action.payload;
        const id = updated._id;

        const index = state.subscriptions.findIndex((s) => s._id === id);
        if (index !== -1) {
          state.subscriptions[index] = { ...state.subscriptions[index], ...updated };
        }
        if (state.currentSubscription?._id === id) {
          state.currentSubscription = { ...state.currentSubscription, ...updated };
        }
        state.lastActionSubId = null;
      })
      .addCase(cancelSubscriptionAdmin.rejected, (state) => {
        state.actionLoading = false;
        state.lastActionSubId = null;
      });

    // expireSubscriptionAdmin
    builder
      .addCase(expireSubscriptionAdmin.pending, (state, action) => {
        state.actionLoading = true;
        state.lastActionSubId = action.meta.arg.subId;
      })
      .addCase(expireSubscriptionAdmin.fulfilled, (state, action) => {
        state.actionLoading = false;
        const updated = action.payload;
        const id = updated._id;

        const index = state.subscriptions.findIndex((s) => s._id === id);
        if (index !== -1) {
          state.subscriptions[index] = { ...state.subscriptions[index], ...updated };
        }
        if (state.currentSubscription?._id === id) {
          state.currentSubscription = { ...state.currentSubscription, ...updated };
        }
        state.lastActionSubId = null;
      })
      .addCase(expireSubscriptionAdmin.rejected, (state) => {
        state.actionLoading = false;
        state.lastActionSubId = null;
      });

    // getSubscriptionStats
    builder
      .addCase(getSubscriptionStats.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSubscriptionStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload;
      })
      .addCase(getSubscriptionStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearAdminSubscriptionError, clearCurrentSubscription } =
  adminSubscriptionSlice.actions;

// ────────────────────────────────────────────────
// Selectors
// ────────────────────────────────────────────────

export const selectAdminSubscriptions = (state) => state.adminSubscription.subscriptions;
export const selectAdminSubscriptionsTotal = (state) => state.adminSubscription.total;
export const selectAdminSubscriptionPagination = (state) => state.adminSubscription.pagination;
export const selectCurrentAdminSubscription = (state) => state.adminSubscription.currentSubscription;
export const selectAdminSubscriptionLoading = (state) => state.adminSubscription.loading;
export const selectAdminSubscriptionActionLoading = (state) => state.adminSubscription.actionLoading;
export const selectAdminSubscriptionError = (state) => state.adminSubscription.error;
export const selectLastActionSubId = (state) => state.adminSubscription.lastActionSubId;
export const selectSubscriptionStats = (state) => state.adminSubscription.stats;

export default adminSubscriptionSlice.reducer;
